import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { 
  Plus, Trash2, Calculator, Upload, FileText, Image as ImageIcon, 
  Download, Send, Save, Eye, X, DollarSign, Percent,
  Search, User, Home, MapPin, Loader2, CheckCircle, Clock,
  AlertCircle, Phone, Mail, Globe, BookTemplate, Bookmark, Briefcase
} from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

// Default contract disclaimer text
const DEFAULT_DISCLAIMER = `**Terms & Conditions:**

- This quote is valid for 30 days from the date of issue.
- 50% deposit required to schedule work. Balance due upon completion.
- All materials and workmanship are guaranteed for 1 year.
- Any changes to the scope of work may result in additional charges.
- Client is responsible for obtaining any necessary permits.
- Work hours are Monday-Friday 8am-5pm unless otherwise agreed.`;

export default function AdvancedQuoteCreator({ 
  open, 
  onOpenChange, 
  lead, 
  existingQuote = null,
  onSuccess,
  enableCustomerSelection = false,
  jobMode = false
}) {
  const fileInputRef = useRef(null);
  const itemPhotoInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [approvalHistory, setApprovalHistory] = useState(null);
  const [uploadingItemPhoto, setUploadingItemPhoto] = useState(null);

  // Template states
  const [templates, setTemplates] = useState([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);

  // Job mode states
  const [handymen, setHandymen] = useState([]);
  const [jobAssignment, setJobAssignment] = useState({
    handyman_id: '',
    scheduled_date: '',
    job_notes: ''
  });
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Customer & Property Selection States
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerProperties, setCustomerProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [newPropertyForm, setNewPropertyForm] = useState({
    property_name: 'Home',
    address_line_1: '',
    city: ''
  });

  // Package Billing States
  const [customerPackages, setCustomerPackages] = useState([]);
  const [usePackage, setUsePackage] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState('');

  // Quote Header Data
  const [quoteData, setQuoteData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    service_address: '',
    service_type: '',
    prepared_by: '',
    valid_days: 30,
    notes: '',
    internal_notes: ''
  });

  // Line Items with description and photos
  const [items, setItems] = useState([{
    id: `item_${Date.now()}`,
    name: '',
    description: '',
    quantity: 1,
    unit: 'unit',
    unit_cost: 0,
    markup_percent: 30,
    unit_price: 0,
    total: 0,
    is_optional: false,
    photos: []
  }]);

  // Payment Terms
  const [paymentTerms, setPaymentTerms] = useState({
    advance_type: 'percent',
    advance_value: 50,
    advance_amount: 0,
    interim_type: 'percent',
    interim_value: 0,
    interim_amount: 0,
    final_type: 'percent',
    final_value: 50,
    final_amount: 0
  });

  // Contract/Disclaimer
  const [contractDisclaimer, setContractDisclaimer] = useState(DEFAULT_DISCLAIMER);

  // Tax & Discount
  const [discount, setDiscount] = useState({ type: 'amount', value: 0 });
  const [tax, setTax] = useState({ percent: 0, value: 0 });

  // Attachments
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Rich Text Editor Config - Using Textarea for React 19 compatibility
  const formatDescription = (text) => {
    // Convert markdown-like syntax to simple HTML
    if (!text) return '';
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^- /gm, '• ')
      .replace(/\n/g, '<br/>');
  };

  // Fetch customers when enableCustomerSelection is true
  useEffect(() => {
    if (enableCustomerSelection && open) {
      fetchCustomers();
    }
    if (jobMode && open) {
      axiosInstance.get('/handymen').then(res => setHandymen(res.data || [])).catch(() => {});
    }
  }, [enableCustomerSelection, open]);

  // Fetch templates when dialog opens
  useEffect(() => {
    if (open && !existingQuote) {
      fetchTemplates();
    }
  }, [open, existingQuote]);

  // Fetch customer properties when customer is selected
  useEffect(() => {
    if (selectedCustomer?.id) {
      fetchCustomerProperties(selectedCustomer.id);
      fetchCustomerPackages(selectedCustomer.id);
    } else {
      setCustomerProperties([]);
      setSelectedPropertyId('');
      setCustomerPackages([]);
      setUsePackage(false);
      setSelectedPackageId('');
    }
  }, [selectedCustomer]);

  const fetchTemplates = async () => {
    try {
      const response = await axiosInstance.get('/quote-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates');
    }
  };

  const fetchCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const response = await axiosInstance.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchCustomerProperties = async (customerId) => {
    try {
      const response = await axiosInstance.get(`/customers/${customerId}/profile`);
      const properties = response.data.properties || [];
      setCustomerProperties(properties);
      
      // Auto-select primary property
      const primaryProp = properties.find(p => p.is_primary);
      if (primaryProp) {
        setSelectedPropertyId(primaryProp.id);
        setQuoteData(prev => ({
          ...prev,
          service_address: primaryProp.full_address
        }));
      }
    } catch (error) {
      console.error('Failed to fetch customer properties');
    }
  };

  const fetchCustomerPackages = async (customerId) => {
    try {
      const response = await axiosInstance.get(`/packages/customer/${customerId}/active`);
      setCustomerPackages(response.data || []);
      if (response.data?.length === 1) {
        setSelectedPackageId(response.data[0].id);
      }
    } catch (error) {
      setCustomerPackages([]);
    }
  };

  // Filter customers based on search
  const filteredCustomers = customers.filter(customer => {
    const search = customerSearch.toLowerCase();
    return (
      customer.name?.toLowerCase().includes(search) ||
      customer.email?.toLowerCase().includes(search) ||
      customer.phone?.includes(search)
    );
  });

  // Handle customer selection
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setQuoteData(prev => ({
      ...prev,
      customer_name: customer.name,
      customer_email: customer.email,
      customer_phone: customer.phone || '',
      customer_address: customer.address || ''
    }));
    setCustomerSearch('');
    setShowCustomerDropdown(false);
  };

  // Handle property selection
  const handlePropertyChange = (propertyId) => {
    setSelectedPropertyId(propertyId);
    const property = customerProperties.find(p => p.id === propertyId);
    if (property) {
      setQuoteData(prev => ({
        ...prev,
        customer_address: property.full_address,
        service_address: property.full_address
      }));
    }
  };

  // Add new property inline
  const handleAddProperty = async () => {
    const custId = selectedCustomer?.id || lead?.customer_id;
    if (!custId || !newPropertyForm.address_line_1) {
      toast.error('Please enter property address');
      return;
    }
    
    try {
      await axiosInstance.post(`/customers/${custId}/properties`, newPropertyForm);
      toast.success('Property added');
      await fetchCustomerProperties(custId);
      setShowAddProperty(false);
      setNewPropertyForm({ property_name: 'Home', address_line_1: '', city: '' });
    } catch (error) {
      toast.error('Failed to add property');
    }
  };

  // Initialize data when lead changes
  useEffect(() => {
    if (lead) {
      const leadData = lead.lead_data || {};
      setQuoteData(prev => ({
        ...prev,
        customer_name: leadData.full_name || leadData.name || '',
        customer_email: leadData.email || '',
        customer_phone: leadData.phone || '',
        customer_address: leadData.address || '',
        service_address: leadData.address || '',
        service_type: leadData.service_needed || ''
      }));

      // If lead has customer_id, fetch properties for property selection
      if (lead.customer_id) {
        fetchCustomerProperties(lead.customer_id);
        fetchCustomerPackages(lead.customer_id);
      }

      // Prefill from visit report if available
      if (lead.prefill_line_items && lead.prefill_line_items.length > 0) {
        const prefillItems = lead.prefill_line_items.map((item, idx) => ({
          id: `item_${Date.now()}_${idx}`,
          name: item.description || '',
          description: item.notes || '',
          quantity: 1,
          unit: 'unit',
          unit_cost: 0,
          markup_percent: 30,
          unit_price: 0,
          total: 0,
          is_optional: false,
          photos: item.photos || []
        }));
        setItems(prefillItems);
        if (lead.prefill_notes) {
          setQuoteData(prev => ({ ...prev, notes: lead.prefill_notes }));
        }
      }
    }

    // Get admin user
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setAdminUser(user);
    setQuoteData(prev => ({
      ...prev,
      prepared_by: user.name || user.email || 'Admin'
    }));
  }, [lead]);

  // Reset form when dialog opens for new quote
  useEffect(() => {
    if (open && !existingQuote && !lead) {
      // Reset for new quote creation
      setSelectedCustomer(null);
      setSelectedPropertyId('');
      setCustomerSearch('');
      setCustomerPackages([]);
      setUsePackage(false);
      setSelectedPackageId('');
      setItems([{
        id: `item_${Date.now()}`,
        name: '',
        description: '',
        quantity: 1,
        unit: 'unit',
        unit_cost: 0,
        markup_percent: 30,
        unit_price: 0,
        total: 0,
        is_optional: false,
        photos: []
      }]);
      setDiscount({ type: 'amount', value: 0 });
      setTax({ percent: 0, value: 0 });
      setAttachments([]);
      setPaymentTerms({
        advance_type: 'percent',
        advance_value: 50,
        advance_amount: 0,
        interim_type: 'percent',
        interim_value: 0,
        interim_amount: 0,
        final_type: 'percent',
        final_value: 50,
        final_amount: 0
      });
      setContractDisclaimer(DEFAULT_DISCLAIMER);
      
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setQuoteData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        customer_address: '',
        service_address: '',
        service_type: '',
        prepared_by: user.name || user.email || 'Admin',
        valid_days: 30,
        notes: '',
        internal_notes: ''
      });
    }
  }, [open, existingQuote, lead]);

  // Load existing quote data if editing
  useEffect(() => {
    if (existingQuote) {
      setQuoteData({
        customer_name: existingQuote.customer_name || '',
        customer_email: existingQuote.customer_email || '',
        customer_phone: existingQuote.customer_phone || '',
        customer_address: existingQuote.customer_address || '',
        service_address: existingQuote.service_address || '',
        service_type: existingQuote.service_type || '',
        prepared_by: existingQuote.prepared_by || '',
        valid_days: 30,
        notes: existingQuote.notes || '',
        internal_notes: existingQuote.internal_notes || ''
      });
      
      if (existingQuote.items?.length > 0) {
        setItems(existingQuote.items.map((item, idx) => ({
          id: item.id || `item_${Date.now()}_${idx}`,
          name: item.name || item.service || '',
          description: item.description || '',
          quantity: item.quantity || item.hours || 1,
          unit: item.unit || 'unit',
          unit_cost: item.unit_cost || 0,
          markup_percent: item.markup_percent || 30,
          unit_price: item.unit_price || item.rate || 0,
          total: item.total || item.amount || 0,
          is_optional: item.is_optional || false,
          photos: item.photos || [],
          deduct_from_package: item.deduct_from_package || false
        })));
      }
      
      setDiscount({
        type: existingQuote.discount_type || 'amount',
        value: existingQuote.discount_type === 'percent' 
          ? existingQuote.discount_percent || 0 
          : existingQuote.discount || 0
      });
      
      setTax({
        percent: existingQuote.tax_percent || 0,
        value: existingQuote.tax || 0
      });
      
      setAttachments(existingQuote.attachments || []);
      
      // Load payment terms
      if (existingQuote.payment_terms) {
        setPaymentTerms(existingQuote.payment_terms);
      }
      
      // Load contract disclaimer
      if (existingQuote.contract_disclaimer) {
        setContractDisclaimer(existingQuote.contract_disclaimer);
      }
      
      // Fetch approval history for existing quote
      if (existingQuote.id) {
        fetchApprovalHistory(existingQuote.id);
      }

      // Load package billing state
      if (existingQuote.use_package) {
        setUsePackage(true);
        setSelectedPackageId(existingQuote.customer_package_id || '');
        // Fetch packages for this customer
        const custId = existingQuote.customer_id;
        if (custId) {
          fetchCustomerPackages(custId);
        }
      }
    }
  }, [existingQuote]);

  // Fetch approval history
  const fetchApprovalHistory = async (quoteId) => {
    try {
      const response = await axiosInstance.get(`/quotes/${quoteId}/approval-history`);
      setApprovalHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch approval history');
    }
  };

  // Format approval method for display
  const formatApprovalMethod = (method) => {
    const methods = {
      'other_communication': 'Other Communication (Phone, etc.)',
      'email': 'Email Confirmation',
      'portal': 'Customer Portal'
    };
    return methods[method] || method;
  };

  // Get method icon
  const getMethodIcon = (method) => {
    switch (method) {
      case 'other_communication': return <Phone className="w-4 h-4" />;
      case 'email': return <Mail className="w-4 h-4" />;
      case 'portal': return <Globe className="w-4 h-4" />;
      default: return <CheckCircle className="w-4 h-4" />;
    }
  };

  // Calculate totals
  const subtotal = items.reduce((sum, item) => sum + (item.is_optional ? 0 : item.total), 0);
  const discountAmount = discount.type === 'percent' 
    ? (subtotal * discount.value / 100) 
    : discount.value;
  const taxableAmount = subtotal - discountAmount;
  const taxAmount = taxableAmount * (tax.percent / 100);
  const grandTotal = taxableAmount + taxAmount;

  // Recalculate payment terms when grand total changes
  useEffect(() => {
    setPaymentTerms(prev => {
      const advance = prev.advance_type === 'percent' 
        ? grandTotal * prev.advance_value / 100 
        : prev.advance_value;
      const interim = prev.interim_type === 'percent' 
        ? grandTotal * prev.interim_value / 100 
        : prev.interim_value;
      const final = prev.final_type === 'percent' 
        ? grandTotal * prev.final_value / 100 
        : prev.final_value;
      
      return {
        ...prev,
        advance_amount: advance,
        interim_amount: interim,
        final_amount: final
      };
    });
  }, [grandTotal, paymentTerms.advance_type, paymentTerms.advance_value, paymentTerms.interim_type, paymentTerms.interim_value, paymentTerms.final_type, paymentTerms.final_value]);

  // Update item
  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    // Recalculate unit_price and total if cost or markup changes
    if (field === 'unit_cost' || field === 'markup_percent') {
      const cost = field === 'unit_cost' ? value : newItems[index].unit_cost;
      const markup = field === 'markup_percent' ? value : newItems[index].markup_percent;
      newItems[index].unit_price = cost * (1 + markup / 100);
    }
    
    // Recalculate total if quantity or unit_price changes
    if (field === 'quantity' || field === 'unit_price' || field === 'unit_cost' || field === 'markup_percent') {
      // If deducted from package, total stays 0
      if (newItems[index].deduct_from_package) {
        newItems[index].total = 0;
      } else {
        newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
      }
    }

    // When toggling deduct_from_package
    if (field === 'deduct_from_package') {
      if (value) {
        newItems[index]._original_total = newItems[index].total;
        newItems[index].total = 0;
      } else {
        newItems[index].total = newItems[index]._original_total || (newItems[index].quantity * newItems[index].unit_price);
        delete newItems[index]._original_total;
      }
    }
    
    setItems(newItems);
  };

  // Add new item
  const addItem = () => {
    setItems([...items, {
      id: `item_${Date.now()}`,
      name: '',
      description: '',
      quantity: 1,
      unit: 'unit',
      unit_cost: 0,
      markup_percent: 30,
      unit_price: 0,
      total: 0,
      is_optional: false,
      photos: []
    }]);
  };

  // Remove item
  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  // Open pricing calculator
  const openPricingDialog = (index) => {
    setCurrentItemIndex(index);
    setShowPricingDialog(true);
  };

  // Handle line item photo upload
  const handleItemPhotoUpload = async (e, itemIndex) => {
    const files = e.target.files;
    if (!files?.length) return;

    const file = files[0];
    
    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5MB limit');
      return;
    }
    
    setUploadingItemPhoto(itemIndex);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axiosInstance.post('/quotes/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Add photo URL to item
      const newItems = [...items];
      newItems[itemIndex].photos = [...(newItems[itemIndex].photos || []), response.data.url];
      setItems(newItems);
      
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingItemPhoto(null);
      if (itemPhotoInputRef.current) itemPhotoInputRef.current.value = '';
    }
  };

  // Remove item photo
  const removeItemPhoto = (itemIndex, photoIndex) => {
    const newItems = [...items];
    newItems[itemIndex].photos = newItems[itemIndex].photos.filter((_, i) => i !== photoIndex);
    setItems(newItems);
  };

  // Handle file upload (attachments)
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axiosInstance.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setAttachments(prev => [...prev, {
          filename: file.name,
          url: response.data.url,
          type: file.type.startsWith('image/') ? 'image' : 'document'
        }]);
      }
      toast.success('Files uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  // Load template into form
  const loadTemplate = (template) => {
    // Load line items from template
    if (template.items?.length > 0) {
      setItems(template.items.map((item, idx) => ({
        id: `item_${Date.now()}_${idx}`,
        name: item.name || '',
        description: item.description || '',
        quantity: item.quantity || 1,
        unit: item.unit || 'unit',
        unit_cost: item.unit_cost || 0,
        markup_percent: item.markup_percent || 30,
        unit_price: item.unit_price || 0,
        total: (item.quantity || 1) * (item.unit_price || 0),
        is_optional: item.is_optional || false,
        photos: []
      })));
    }
    
    // Load service type
    if (template.service_type) {
      setQuoteData(prev => ({ ...prev, service_type: template.service_type }));
    }
    
    // Load tax
    setTax({ percent: template.tax_percent || 0, value: 0 });
    
    // Load discount
    setDiscount({
      type: template.discount_type || 'amount',
      value: template.discount_value || 0
    });
    
    // Load payment terms
    if (template.payment_terms) {
      setPaymentTerms(template.payment_terms);
    }
    
    // Load contract disclaimer
    if (template.contract_disclaimer) {
      setContractDisclaimer(template.contract_disclaimer);
    }
    
    // Load valid days
    if (template.valid_days) {
      setQuoteData(prev => ({ ...prev, valid_days: template.valid_days }));
    }
    
    setShowTemplateDialog(false);
    toast.success(`Template "${template.name}" loaded`);
  };

  // Save current quote as template
  const handleSaveAsTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    setSavingTemplate(true);
    try {
      const templateData = {
        name: newTemplateName.trim(),
        description: newTemplateDescription.trim() || null,
        service_type: quoteData.service_type || null,
        items: items.map(item => ({
          name: item.name,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          unit_price: item.unit_price,
          is_optional: item.is_optional
        })),
        tax_percent: tax.percent,
        discount_type: discount.type,
        discount_value: discount.value,
        payment_terms: paymentTerms,
        contract_disclaimer: contractDisclaimer,
        valid_days: quoteData.valid_days
      };
      
      await axiosInstance.post('/quote-templates', templateData);
      toast.success('Template saved successfully');
      setShowSaveTemplateDialog(false);
      setNewTemplateName('');
      setNewTemplateDescription('');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Validate payment terms sum to 100%
  const validatePaymentTerms = () => {
    const total = paymentTerms.advance_amount + paymentTerms.interim_amount + paymentTerms.final_amount;
    const diff = Math.abs(total - grandTotal);
    return diff < 0.01; // Allow small floating point differences
  };

  // Save quote (draft)
  const handleSave = async (sendAfterSave = false) => {
    if (!quoteData.customer_name || !quoteData.customer_email) {
      toast.error('Please fill in customer name and email');
      return;
    }

    if (items.some(item => !item.name)) {
      toast.error('Please fill in all line item names');
      return;
    }

    // Validate payment terms
    if (!validatePaymentTerms()) {
      toast.error('Payment terms must sum to 100% of the total');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        lead_id: lead?.id || null,
        customer_id: selectedCustomer?.id || existingQuote?.customer_id || null,
        property_id: selectedPropertyId || existingQuote?.property_id || null,
        property_name: customerProperties.find(p => p.id === selectedPropertyId)?.property_name || existingQuote?.property_name || null,
        customer_name: quoteData.customer_name,
        customer_email: quoteData.customer_email,
        customer_phone: quoteData.customer_phone,
        customer_address: quoteData.customer_address,
        service_address: quoteData.service_address,
        service_type: quoteData.service_type,
        prepared_by: quoteData.prepared_by,
        items: items.map(item => ({
          id: item.id,
          name: item.name,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          unit_cost: item.unit_cost,
          markup_percent: item.markup_percent,
          unit_price: item.unit_price,
          total: item.total,
          is_optional: item.is_optional,
          photos: item.photos,
          deduct_from_package: item.deduct_from_package || false
        })),
        subtotal,
        tax: taxAmount,
        tax_percent: tax.percent,
        discount: discountAmount,
        discount_type: discount.type,
        discount_percent: discount.type === 'percent' ? discount.value : 0,
        total_amount: grandTotal,
        payment_terms: paymentTerms,
        contract_disclaimer: contractDisclaimer,
        notes: quoteData.notes,
        internal_notes: quoteData.internal_notes,
        attachments,
        valid_days: quoteData.valid_days,
        use_package: usePackage,
        customer_package_id: usePackage ? selectedPackageId : null
      };

      let quoteId;
      
      if (existingQuote) {
        await axiosInstance.patch(`/quotes/${existingQuote.id}`, payload);
        quoteId = existingQuote.id;
        toast.success('Quote updated successfully');
      } else {
        const response = await axiosInstance.post('/quotes', payload);
        quoteId = response.data.quote_id;
        toast.success('Quote created successfully');
      }

      if (sendAfterSave && quoteId) {
        await axiosInstance.post(`/quotes/${quoteId}/send`, {
          base_url: window.location.origin
        });
        toast.success('Quote sent to customer for approval');
      }

      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to save quote');
    } finally {
      setLoading(false);
    }
  };

  // Job Mode: Create quote + auto-approve + convert to job
  const handleCreateJob = async () => {
    if (!quoteData.customer_name || items.length === 0 || !items[0].name) {
      toast.error('Please fill customer name and at least one line item');
      return;
    }

    if (!jobAssignment.handyman_id || !jobAssignment.scheduled_date) {
      toast.error('Please assign a handyman and scheduled date to create a job');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        lead_id: lead?.id || null,
        customer_id: selectedCustomer?.id || lead?.customer_id || null,
        property_id: selectedPropertyId || null,
        property_name: customerProperties.find(p => p.id === selectedPropertyId)?.property_name || null,
        customer_name: quoteData.customer_name,
        customer_email: quoteData.customer_email,
        customer_phone: quoteData.customer_phone,
        customer_address: quoteData.customer_address,
        service_address: quoteData.service_address,
        service_type: quoteData.service_type,
        prepared_by: quoteData.prepared_by,
        items: items.map(item => ({
          id: item.id, name: item.name, description: item.description,
          quantity: item.quantity, unit: item.unit, unit_cost: item.unit_cost,
          markup_percent: item.markup_percent, unit_price: item.unit_price,
          total: item.total, is_optional: item.is_optional, photos: item.photos,
          deduct_from_package: item.deduct_from_package || false
        })),
        subtotal,
        tax: taxAmount,
        tax_percent: tax.percent,
        discount: discountAmount,
        discount_type: discount.type,
        discount_percent: discount.type === 'percent' ? discount.value : 0,
        total_amount: grandTotal,
        payment_terms: paymentTerms,
        contract_disclaimer: contractDisclaimer,
        notes: quoteData.notes,
        internal_notes: quoteData.internal_notes,
        attachments,
        valid_days: quoteData.valid_days,
        status: 'approved',
        use_package: usePackage,
        customer_package_id: usePackage ? selectedPackageId : null
      };

      // Step 1: Create quote (auto-approved)
      const quoteRes = await axiosInstance.post('/quotes', payload);
      const quoteId = quoteRes.data.quote_id;

      // Step 2: Convert to job with assignment
      const convertPayload = {
        handyman_id: jobAssignment.handyman_id,
        scheduled_date: new Date(jobAssignment.scheduled_date).toISOString()
      };
      if (jobAssignment.job_notes) convertPayload.job_notes = jobAssignment.job_notes;

      await axiosInstance.post(`/quotes/${quoteId}/convert-to-job`, convertPayload);

      toast.success('Job created successfully!');
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to create job');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  // Download PDF
  const handleDownloadPDF = async () => {
    if (!existingQuote?.id) {
      toast.error('Please save the quote first');
      return;
    }

    try {
      const response = await axiosInstance.get(`/quotes/${existingQuote.id}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quote_${existingQuote.quote_number || existingQuote.id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-xl font-bold">
                  {jobMode ? 'Create Job' : (existingQuote ? 'Edit Quote' : 'Create Quote')}
                </DialogTitle>
                <DialogDescription>
                  {jobMode && 'Fill in details to create a new job'}
                  {!jobMode && lead && `Lead: ${lead.lead_data?.full_name || lead.lead_data?.name || 'Customer'}`}
                  {!jobMode && !lead && !existingQuote && enableCustomerSelection && 'Select a customer and create a new quote'}
                </DialogDescription>
              </div>
              
              {/* Template Buttons - Only for new quotes */}
              {!existingQuote && (
                <div className="flex gap-2">
                  {templates.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowTemplateDialog(true)}
                      className="text-purple-600 border-purple-200 hover:bg-purple-50"
                      data-testid="load-template-btn"
                    >
                      <Bookmark className="w-4 h-4 mr-1" />
                      Load Template
                    </Button>
                  )}
                </div>
              )}
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-6 pr-2">
            {/* Customer Selection - Only show when enableCustomerSelection is true and no lead/existingQuote */}
            {enableCustomerSelection && !lead && !existingQuote && (
              <Card className="border-gray-300 bg-gray-100/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="w-4 h-4 text-[#595959]" />
                    Customer Selection
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Customer Search */}
                  <div className="relative">
                    <Label>Search Customer *</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Type to search by name, email, or phone..."
                        value={selectedCustomer ? selectedCustomer.name : customerSearch}
                        onChange={(e) => {
                          setCustomerSearch(e.target.value);
                          setSelectedCustomer(null);
                          setShowCustomerDropdown(true);
                        }}
                        onFocus={() => setShowCustomerDropdown(true)}
                        className="pl-10"
                        data-testid="customer-search-input"
                      />
                      {selectedCustomer && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                          onClick={() => {
                            setSelectedCustomer(null);
                            setCustomerSearch('');
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    {/* Customer Dropdown */}
                    {showCustomerDropdown && !selectedCustomer && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {loadingCustomers ? (
                          <div className="p-4 text-center text-gray-500">
                            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                          </div>
                        ) : filteredCustomers.length > 0 ? (
                          filteredCustomers.slice(0, 10).map(customer => (
                            <div
                              key={customer.id}
                              className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                              onClick={() => handleSelectCustomer(customer)}
                              data-testid={`customer-option-${customer.id}`}
                            >
                              <div className="font-medium">{customer.name}</div>
                              <div className="text-sm text-gray-500">{customer.email}</div>
                              {customer.phone && <div className="text-sm text-gray-400">{customer.phone}</div>}
                            </div>
                          ))
                        ) : customerSearch ? (
                          <div className="p-4 text-center text-gray-500">
                            No customers found
                          </div>
                        ) : (
                          <div className="p-4 text-center text-gray-500">
                            Start typing to search customers
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Selected Customer Info */}
                  {selectedCustomer && (
                    <div className="p-3 bg-white rounded-lg border border-gray-300">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-[#595959]" />
                        </div>
                        <div>
                          <p className="font-medium">{selectedCustomer.name}</p>
                          <p className="text-sm text-gray-500">{selectedCustomer.email}</p>
                        </div>
                        <Badge className="ml-auto bg-gray-200 text-[#595959]">Selected</Badge>
                      </div>
                    </div>
                  )}

                  {/* Property Selection */}
                  {(selectedCustomer || lead?.customer_id) && (
                    <div>
                      <Label className="flex items-center gap-2">
                        <Home className="w-4 h-4" />
                        Service Property
                      </Label>
                      <div className="flex gap-2">
                        <Select value={selectedPropertyId} onValueChange={handlePropertyChange}>
                          <SelectTrigger className="flex-1" data-testid="property-select">
                            <SelectValue placeholder="Select property" />
                          </SelectTrigger>
                          <SelectContent>
                            {customerProperties.map(property => (
                              <SelectItem key={property.id} value={property.id}>
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-3 h-3" />
                                  {property.property_name}: {property.full_address?.substring(0, 40)}...
                                  {property.is_primary && <Badge className="ml-2 text-xs">Primary</Badge>}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowAddProperty(true)}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Add Property Inline Form */}
                      {showAddProperty && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg border space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">Add New Property</Label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowAddProperty(false)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <Input
                              placeholder="Name (Home, Office...)"
                              value={newPropertyForm.property_name}
                              onChange={(e) => setNewPropertyForm({...newPropertyForm, property_name: e.target.value})}
                            />
                            <Input
                              placeholder="Address *"
                              value={newPropertyForm.address_line_1}
                              onChange={(e) => setNewPropertyForm({...newPropertyForm, address_line_1: e.target.value})}
                              className="col-span-2"
                            />
                          </div>
                          <div className="flex justify-end">
                            <Button size="sm" onClick={handleAddProperty} className="bg-[#595959] hover:bg-[#404040]">
                              Add Property
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Package Billing Banner */}
            {customerPackages.length > 0 && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="font-semibold text-blue-900 text-sm">
                      Active Hour Package{customerPackages.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  {customerPackages.map(pkg => (
                    <div key={pkg.id} className="flex items-center justify-between text-sm bg-white p-2 rounded border border-blue-100">
                      <span className="font-medium text-gray-800">{pkg.package_name}</span>
                      <div className="flex gap-3 text-xs">
                        <span>Total: <strong>{pkg.total_hours}h</strong></span>
                        <span>Used: <strong>{pkg.used_hours}h</strong></span>
                        <span className="text-blue-700">Remaining: <strong>{pkg.remaining_hours}h</strong></span>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center gap-4 pt-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="package_billing"
                        checked={!usePackage}
                        onChange={() => {
                          setUsePackage(false);
                          // Reset all deduct flags
                          setItems(prev => prev.map(item => {
                            if (item.deduct_from_package) {
                              return { ...item, deduct_from_package: false, total: item._original_total || (item.quantity * item.unit_price) };
                            }
                            return item;
                          }));
                        }}
                        className="accent-gray-600"
                        data-testid="bill-without-package-radio"
                      />
                      <span className="text-sm text-gray-700">Bill without Package Hours</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="package_billing"
                        checked={usePackage}
                        onChange={() => setUsePackage(true)}
                        className="accent-blue-600"
                        data-testid="bill-with-package-radio"
                      />
                      <span className="text-sm font-medium text-blue-800">Bill with Package Hours</span>
                    </label>
                  </div>
                  {usePackage && customerPackages.length > 1 && (
                    <div>
                      <Label className="text-xs text-blue-800">Select Package</Label>
                      <Select value={selectedPackageId} onValueChange={setSelectedPackageId}>
                        <SelectTrigger className="bg-white" data-testid="package-select">
                          <SelectValue placeholder="Choose a package" />
                        </SelectTrigger>
                        <SelectContent>
                          {customerPackages.map(pkg => (
                            <SelectItem key={pkg.id} value={pkg.id}>
                              {pkg.package_name} — {pkg.remaining_hours}h remaining
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Quote Header */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Quote Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Customer Name *</Label>
                    <Input
                      value={quoteData.customer_name}
                      onChange={(e) => setQuoteData({...quoteData, customer_name: e.target.value})}
                      placeholder="Customer name"
                      disabled={enableCustomerSelection && selectedCustomer}
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={quoteData.customer_email}
                      onChange={(e) => setQuoteData({...quoteData, customer_email: e.target.value})}
                      placeholder="customer@email.com"
                      disabled={enableCustomerSelection && selectedCustomer}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={quoteData.customer_phone}
                      onChange={(e) => setQuoteData({...quoteData, customer_phone: e.target.value})}
                      placeholder="+91 XXXXX XXXXX"
                      disabled={enableCustomerSelection && selectedCustomer}
                    />
                  </div>
                  
                  {/* Property Selection - shown when lead has customer_id */}
                  {lead?.customer_id && customerProperties.length > 0 && (
                    <div className="col-span-2 md:col-span-3">
                      <Label className="flex items-center gap-2">
                        <Home className="w-4 h-4" />
                        Select Property
                      </Label>
                      <div className="flex gap-2">
                        <Select value={selectedPropertyId} onValueChange={handlePropertyChange} data-testid="lead-property-select">
                          <SelectTrigger className="flex-1" data-testid="property-select">
                            <SelectValue placeholder="Select a property for this quote" />
                          </SelectTrigger>
                          <SelectContent>
                            {customerProperties.map(property => (
                              <SelectItem key={property.id} value={property.id}>
                                {property.property_name}: {property.full_address}
                                {property.is_primary ? ' (Primary)' : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label>Billing Address</Label>
                    <Input
                      value={quoteData.customer_address}
                      onChange={(e) => setQuoteData({...quoteData, customer_address: e.target.value})}
                      placeholder="Billing address"
                    />
                  </div>
                  <div>
                    <Label>Service Address</Label>
                    <Input
                      value={quoteData.service_address}
                      onChange={(e) => setQuoteData({...quoteData, service_address: e.target.value})}
                      placeholder="Service address (if different)"
                    />
                  </div>
                  <div>
                    <Label>Service Type</Label>
                    <Input
                      value={quoteData.service_type}
                      onChange={(e) => setQuoteData({...quoteData, service_type: e.target.value})}
                      placeholder="e.g., Interior Painting"
                    />
                  </div>
                  <div>
                    <Label>Prepared By</Label>
                    <Input
                      value={quoteData.prepared_by}
                      onChange={(e) => setQuoteData({...quoteData, prepared_by: e.target.value})}
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <Label>Valid For (days)</Label>
                    <Input
                      type="number"
                      value={quoteData.valid_days}
                      onChange={(e) => setQuoteData({...quoteData, valid_days: parseInt(e.target.value) || 30})}
                    />
                  </div>
                  {existingQuote && (
                    <div>
                      <Label>Quote Number</Label>
                      <Input
                        value={existingQuote.quote_number || existingQuote.id.slice(0, 8).toUpperCase()}
                        disabled
                        className="bg-gray-50"
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Line Items</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-2 text-sm font-medium text-gray-600 px-2">
                  <div className="col-span-4">Product / Service</div>
                  <div className="col-span-2 text-center">Qty</div>
                  <div className="col-span-2 text-right">Unit Price</div>
                  <div className="col-span-2 text-right">Total</div>
                  <div className="col-span-2"></div>
                </div>

                {/* Line Items */}
                {items.map((item, index) => (
                  <div key={item.id} className="space-y-2 p-3 border rounded-lg bg-gray-50/50">
                    {/* Main Row */}
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-4">
                        <Input
                          value={item.name}
                          onChange={(e) => updateItem(index, 'name', e.target.value)}
                          placeholder="Product/Service name *"
                          className={!item.name ? 'border-red-300' : ''}
                          data-testid={`item-name-${index}`}
                        />
                      </div>
                      <div className="col-span-2 flex gap-1">
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 1)}
                          className="text-center"
                        />
                        <Select value={item.unit} onValueChange={(val) => updateItem(index, 'unit', val)}>
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unit">unit</SelectItem>
                            <SelectItem value="hour">hr</SelectItem>
                            <SelectItem value="sqft">sqft</SelectItem>
                            <SelectItem value="day">day</SelectItem>
                            <SelectItem value="each">each</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <div className="relative">
                          <Input
                            type="number"
                            value={item.unit_price.toFixed(2)}
                            onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            className="text-right pr-8"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-2 text-[#595959] hover:text-[#595959]"
                            onClick={() => openPricingDialog(index)}
                            title="Calculate from cost + markup"
                          >
                            <Calculator className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          value={item.total.toFixed(2)}
                          disabled
                          className="text-right bg-gray-100 font-medium"
                        />
                      </div>
                      <div className="col-span-2 flex justify-end gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          disabled={items.length === 1}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Description - Always Visible */}
                    <div className="mt-2">
                      <Textarea
                        value={item.description || ''}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        placeholder="Add detailed description for this line item..."
                        className="bg-white min-h-[60px] text-sm"
                        rows={2}
                        data-testid={`item-description-${index}`}
                      />
                    </div>

                    {/* Photos Section - Always Visible */}
                    <div className="mt-2">
                      <div className="flex flex-wrap items-center gap-2">
                        {item.photos?.map((photo, photoIdx) => (
                          <div key={photoIdx} className="relative group w-16 h-16">
                            <img 
                              src={photo} 
                              alt={`Photo ${photoIdx + 1}`}
                              className="w-full h-full object-cover rounded-lg border"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute -top-2 -right-2 h-5 w-5 p-0 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => removeItemPhoto(index, photoIdx)}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                        
                        {/* Upload Button */}
                        <label className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-teal-400 hover:bg-gray-100 transition-colors">
                          {uploadingItemPhoto === index ? (
                            <Loader2 className="w-4 h-4 animate-spin text-[#595959]" />
                          ) : (
                            <>
                              <ImageIcon className="w-4 h-4 text-gray-400" />
                              <span className="text-xs text-gray-400">Photo</span>
                            </>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleItemPhotoUpload(e, index)}
                            className="hidden"
                            disabled={uploadingItemPhoto === index}
                            data-testid={`item-photo-upload-${index}`}
                          />
                        </label>
                      </div>
                    </div>

                    {/* Optional + Package Checkbox */}
                    <div className="flex items-center gap-4 mt-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`optional-${index}`}
                          checked={item.is_optional}
                          onCheckedChange={(checked) => updateItem(index, 'is_optional', checked)}
                        />
                        <Label htmlFor={`optional-${index}`} className="text-sm text-gray-600 cursor-pointer">
                          Mark as optional
                        </Label>
                      </div>
                      {usePackage && (
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`pkg-deduct-${index}`}
                            checked={item.deduct_from_package || false}
                            onCheckedChange={(checked) => updateItem(index, 'deduct_from_package', checked)}
                            data-testid={`pkg-deduct-checkbox-${index}`}
                          />
                          <Label htmlFor={`pkg-deduct-${index}`} className="text-sm text-blue-700 cursor-pointer font-medium">
                            Deduct from Package
                          </Label>
                          {item.deduct_from_package && (
                            <Badge className="bg-blue-100 text-blue-800 text-xs">PKG</Badge>
                          )}
                        </div>
                      )}
                      {item.unit_cost > 0 && (
                        <Badge variant="outline" className="text-xs">
                          Cost: ${item.unit_cost.toFixed(2)} | Markup: {item.markup_percent}%
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}

                <Button type="button" variant="outline" onClick={addItem} className="w-full" data-testid="add-line-item-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Line Item
                </Button>
              </CardContent>
            </Card>

            {/* Tax & Discount + Totals */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Discount & Tax */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Discount & Tax</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Discount */}
                  <div className="space-y-2">
                    <Label>Discount</Label>
                    <div className="flex gap-2">
                      <Select
                        value={discount.type}
                        onValueChange={(value) => setDiscount({...discount, type: value})}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="amount">$ Amount</SelectItem>
                          <SelectItem value="percent">% Percent</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="relative flex-1">
                        <Input
                          type="number"
                          min="0"
                          value={discount.value}
                          onChange={(e) => setDiscount({...discount, value: parseFloat(e.target.value) || 0})}
                          className="pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                          {discount.type === 'percent' ? <Percent className="w-4 h-4" /> : <DollarSign className="w-4 h-4" />}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Tax */}
                  <div className="space-y-2">
                    <Label>Tax (%)</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={tax.percent}
                        onChange={(e) => setTax({...tax, percent: parseFloat(e.target.value) || 0})}
                        className="w-24"
                      />
                      <span className="flex items-center text-gray-500">%</span>
                      <Input
                        type="number"
                        value={taxAmount.toFixed(2)}
                        disabled
                        className="flex-1 bg-gray-100"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Totals */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Quote Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      Discount {discount.type === 'percent' && `(${discount.value}%)`}
                    </span>
                    <span className="text-red-600">-${discountAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Tax ({tax.percent}%)</span>
                    <span>${taxAmount.toFixed(2)}</span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between text-lg font-bold text-[#595959]">
                    <span>Grand Total</span>
                    <span>${grandTotal.toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Payment Terms */}
            <Card className="border-green-200 bg-green-50/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  Payment Terms
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  {/* Advance */}
                  <div className="space-y-2">
                    <Label className="text-sm">Advance Payment</Label>
                    <div className="flex gap-2">
                      <Select
                        value={paymentTerms.advance_type}
                        onValueChange={(val) => setPaymentTerms({...paymentTerms, advance_type: val})}
                      >
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percent">%</SelectItem>
                          <SelectItem value="amount">$</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        min="0"
                        value={paymentTerms.advance_value}
                        onChange={(e) => setPaymentTerms({...paymentTerms, advance_value: parseFloat(e.target.value) || 0})}
                        data-testid="payment-advance-value"
                      />
                    </div>
                    <div className="text-sm font-medium text-green-700 bg-white rounded px-2 py-1">
                      ${paymentTerms.advance_amount.toFixed(2)}
                    </div>
                  </div>

                  {/* Interim */}
                  <div className="space-y-2">
                    <Label className="text-sm">Interim Payment</Label>
                    <div className="flex gap-2">
                      <Select
                        value={paymentTerms.interim_type}
                        onValueChange={(val) => setPaymentTerms({...paymentTerms, interim_type: val})}
                      >
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percent">%</SelectItem>
                          <SelectItem value="amount">$</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        min="0"
                        value={paymentTerms.interim_value}
                        onChange={(e) => setPaymentTerms({...paymentTerms, interim_value: parseFloat(e.target.value) || 0})}
                        data-testid="payment-interim-value"
                      />
                    </div>
                    <div className="text-sm font-medium text-green-700 bg-white rounded px-2 py-1">
                      ${paymentTerms.interim_amount.toFixed(2)}
                    </div>
                  </div>

                  {/* Final */}
                  <div className="space-y-2">
                    <Label className="text-sm">Final Payment</Label>
                    <div className="flex gap-2">
                      <Select
                        value={paymentTerms.final_type}
                        onValueChange={(val) => setPaymentTerms({...paymentTerms, final_type: val})}
                      >
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percent">%</SelectItem>
                          <SelectItem value="amount">$</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        min="0"
                        value={paymentTerms.final_value}
                        onChange={(e) => setPaymentTerms({...paymentTerms, final_value: parseFloat(e.target.value) || 0})}
                        data-testid="payment-final-value"
                      />
                    </div>
                    <div className="text-sm font-medium text-green-700 bg-white rounded px-2 py-1">
                      ${paymentTerms.final_amount.toFixed(2)}
                    </div>
                  </div>
                </div>
                
                {/* Validation */}
                <div className={`mt-4 p-2 rounded text-sm ${validatePaymentTerms() ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {validatePaymentTerms() 
                    ? `✓ Payment terms total: $${(paymentTerms.advance_amount + paymentTerms.interim_amount + paymentTerms.final_amount).toFixed(2)} = 100%`
                    : `⚠ Payment terms must equal total: $${grandTotal.toFixed(2)} (Current: $${(paymentTerms.advance_amount + paymentTerms.interim_amount + paymentTerms.final_amount).toFixed(2)})`
                  }
                </div>
              </CardContent>
            </Card>

            {/* Contract/Disclaimer */}
            <Card className="border-amber-200 bg-amber-50/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-amber-600" />
                  Contract / Disclaimer
                </CardTitle>
              </CardHeader>
              <CardContent>
              <Textarea
                value={contractDisclaimer}
                onChange={(e) => setContractDisclaimer(e.target.value)}
                placeholder="Enter terms and conditions... (Use **bold**, *italic*, and - for bullets)"
                className="bg-white min-h-[150px]"
                rows={6}
                data-testid="contract-disclaimer-editor"
              />
              <p className="text-xs text-amber-600 mt-2">
                This will appear on the quote PDF and email sent to customers. Use **bold**, *italic*, and - for bullet points.
              </p>
              </CardContent>
            </Card>

            {/* Attachments */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Attachments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-3">
                  {attachments.map((attachment, index) => (
                    <div key={index} className="relative group">
                      <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                        {attachment.type === 'image' ? (
                          <ImageIcon className="w-4 h-4 text-blue-500" />
                        ) : (
                          <FileText className="w-4 h-4 text-orange-500" />
                        )}
                        <span className="text-sm truncate max-w-[150px]">{attachment.filename}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAttachment(index)}
                          className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? 'Uploading...' : 'Upload Files'}
                  </Button>
                  <p className="text-xs text-gray-500 mt-1">
                    Upload site photos, reference images, or documents (PDF, JPG, PNG)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Customer Notes (visible on quote)</Label>
                  <Textarea
                    value={quoteData.notes}
                    onChange={(e) => setQuoteData({...quoteData, notes: e.target.value})}
                    placeholder="Add any notes visible to the customer..."
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Internal Notes (admin only)</Label>
                  <Textarea
                    value={quoteData.internal_notes}
                    onChange={(e) => setQuoteData({...quoteData, internal_notes: e.target.value})}
                    placeholder="Internal notes not visible to customer..."
                    rows={2}
                    className="bg-yellow-50"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Approval History - Only show for existing quotes */}
            {existingQuote && approvalHistory && (approvalHistory.current_status === 'approved' || approvalHistory.current_status === 'rejected' || approvalHistory.current_status === 'revision_requested' || approvalHistory.history?.length > 0) && (
              <Card className="border-l-4 border-l-green-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="w-4 h-4 text-green-600" />
                    Approval History
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Current Status */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className={
                        approvalHistory.current_status === 'approved' ? 'bg-green-100 text-green-800' :
                        approvalHistory.current_status === 'rejected' ? 'bg-red-100 text-red-800' :
                        approvalHistory.current_status === 'revision_requested' ? 'bg-amber-100 text-amber-800' :
                        'bg-gray-100 text-gray-800'
                      }>
                        {approvalHistory.current_status === 'approved' && <CheckCircle className="w-3 h-3 mr-1" />}
                        {approvalHistory.current_status === 'rejected' && <AlertCircle className="w-3 h-3 mr-1" />}
                        {approvalHistory.current_status === 'revision_requested' && <Clock className="w-3 h-3 mr-1" />}
                        {approvalHistory.current_status?.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                    
                    {approvalHistory.approved_by_name && (
                      <div className="space-y-2 text-sm">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                          <div>
                            <span className="text-gray-500">Approved By:</span>
                            <p className="font-medium">{approvalHistory.approved_by_name}</p>
                            <p className="text-xs text-gray-400">({approvalHistory.approved_by_type === 'admin' ? 'Admin' : 'Customer'})</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Email:</span>
                            <p className="font-medium">{approvalHistory.approved_by_email}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Method:</span>
                            <p className="font-medium flex items-center gap-1">
                              {getMethodIcon(approvalHistory.approval_method)}
                              {formatApprovalMethod(approvalHistory.approval_method)}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">Date & Time:</span>
                            <p className="font-medium">
                              {approvalHistory.approved_at ? formatDateTime(approvalHistory.approved_at) : 'N/A'}
                            </p>
                          </div>
                        </div>
                        
                        {approvalHistory.approval_notes && (
                          <div className="mt-3 p-3 bg-white rounded border">
                            <span className="text-gray-500 text-xs">Notes:</span>
                            <p className="text-gray-700 mt-1">{approvalHistory.approval_notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* History Timeline */}
                  {approvalHistory.history && approvalHistory.history.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2 font-medium">Activity Timeline</p>
                      <div className="space-y-2">
                        {approvalHistory.history.map((entry, idx) => (
                          <div key={entry.id || idx} className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              entry.action === 'approved' ? 'bg-green-100 text-green-600' :
                              entry.action === 'rejected' ? 'bg-red-100 text-red-600' :
                              entry.action === 'sent' ? 'bg-blue-100 text-blue-600' :
                              'bg-amber-100 text-amber-600'
                            }`}>
                              {entry.action === 'approved' && <CheckCircle className="w-4 h-4" />}
                              {entry.action === 'rejected' && <AlertCircle className="w-4 h-4" />}
                              {entry.action === 'revision_requested' && <Clock className="w-4 h-4" />}
                              {entry.action === 'sent' && <Send className="w-4 h-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">
                                {entry.action?.replace('_', ' ').charAt(0).toUpperCase() + entry.action?.replace('_', ' ').slice(1)}
                              </p>
                              <p className="text-xs text-gray-500">
                                By {entry.approved_by_name} ({entry.approved_by_type}) via {formatApprovalMethod(entry.approval_method)}
                              </p>
                              <p className="text-xs text-gray-400">
                                {formatDateTime(entry.timestamp)}
                              </p>
                              {entry.approval_notes && (
                                <p className="text-xs text-gray-600 mt-1 italic">"{entry.approval_notes}"</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Job Assignment Section - only in job mode */}
          {jobMode && (
            <Card className="flex-shrink-0 border-blue-200 bg-blue-50/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-blue-600" />
                  Job Assignment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Assign Handyman</Label>
                    <Select value={jobAssignment.handyman_id} onValueChange={(v) => setJobAssignment(prev => ({...prev, handyman_id: v}))}>
                      <SelectTrigger data-testid="job-handyman-select">
                        <SelectValue placeholder="Select handyman (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {handymen.map(h => (
                          <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Scheduled Date & Time</Label>
                    <Input
                      type="datetime-local"
                      value={jobAssignment.scheduled_date}
                      onChange={(e) => setJobAssignment(prev => ({...prev, scheduled_date: e.target.value}))}
                      data-testid="job-scheduled-date"
                    />
                  </div>
                  <div>
                    <Label>Job Notes</Label>
                    <Input
                      placeholder="Special instructions..."
                      value={jobAssignment.job_notes}
                      onChange={(e) => setJobAssignment(prev => ({...prev, job_notes: e.target.value}))}
                      data-testid="job-notes-field"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex-shrink-0 flex flex-wrap gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            {jobMode ? (
              <>
                <div className="flex-1" />
                <Button
                  onClick={handleCreateJob}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="create-job-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Briefcase className="w-4 h-4 mr-2" />}
                  Create Job
                </Button>
              </>
            ) : (
              <>
            {existingQuote && (
              <Button
                variant="outline"
                onClick={handleDownloadPDF}
                disabled={loading}
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            )}
            {/* Save as Template Button */}
            {items.some(item => item.name) && (
              <Button
                variant="outline"
                onClick={() => setShowSaveTemplateDialog(true)}
                disabled={loading}
                className="text-purple-600 border-purple-200 hover:bg-purple-50"
                data-testid="save-as-template-btn"
              >
                <Bookmark className="w-4 h-4 mr-2" />
                Save as Template
              </Button>
            )}
            <div className="flex-1" />
            <Button
              variant="outline"
              onClick={() => handleSave(false)}
              disabled={loading}
              data-testid="save-quote-draft"
            >
              <Save className="w-4 h-4 mr-2" />
              {existingQuote ? 'Update Draft' : 'Save as Draft'}
            </Button>
            <Button
              onClick={() => handleSave(true)}
              disabled={loading}
              className="bg-[#595959] hover:bg-[#404040]"
              data-testid="save-send-quote"
            >
              <Send className="w-4 h-4 mr-2" />
              {existingQuote ? 'Update & Send' : 'Create & Send for Approval'}
            </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Load Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-purple-600" />
              Load Quote Template
            </DialogTitle>
            <DialogDescription>
              Choose a template to pre-fill line items, payment terms, and more.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {templates.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No templates saved yet</p>
            ) : (
              templates.map((template) => (
                <div
                  key={template.id}
                  className="p-4 border rounded-lg hover:border-purple-300 hover:bg-purple-50/50 cursor-pointer transition-colors"
                  onClick={() => loadTemplate(template)}
                  data-testid={`template-${template.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">{template.name}</h4>
                      {template.description && (
                        <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {template.service_type && (
                          <Badge variant="outline" className="text-xs">{template.service_type}</Badge>
                        )}
                        <Badge variant="outline" className="text-xs bg-gray-50">
                          {template.items?.length || 0} line items
                        </Badge>
                        {template.is_default && (
                          <Badge className="text-xs bg-purple-100 text-purple-700">Default</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Save as Template Dialog */}
      <Dialog open={showSaveTemplateDialog} onOpenChange={setShowSaveTemplateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-purple-600" />
              Save as Template
            </DialogTitle>
            <DialogDescription>
              Save this quote configuration as a reusable template.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Template Name *</Label>
              <Input
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="e.g., Standard Interior Painting"
                data-testid="template-name-input"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={newTemplateDescription}
                onChange={(e) => setNewTemplateDescription(e.target.value)}
                placeholder="Describe when to use this template..."
                rows={2}
              />
            </div>
            <div className="p-3 bg-gray-50 rounded-lg text-sm">
              <p className="font-medium text-gray-700 mb-2">This template will include:</p>
              <ul className="text-gray-600 space-y-1">
                <li>• {items.filter(i => i.name).length} line item(s)</li>
                <li>• Tax: {tax.percent}%</li>
                <li>• Discount: {discount.type === 'percent' ? `${discount.value}%` : `$${discount.value}`}</li>
                <li>• Payment terms configuration</li>
                <li>• Contract/disclaimer text</li>
              </ul>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowSaveTemplateDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleSaveAsTemplate} 
                disabled={savingTemplate || !newTemplateName.trim()}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                data-testid="confirm-save-template"
              >
                {savingTemplate ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bookmark className="w-4 h-4 mr-2" />}
                Save Template
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pricing Calculator Dialog */}
      <Dialog open={showPricingDialog} onOpenChange={setShowPricingDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Pricing Calculator</DialogTitle>
            <DialogDescription>
              Calculate unit price from cost and markup (internal use only)
            </DialogDescription>
          </DialogHeader>
          {currentItemIndex !== null && items[currentItemIndex] && (
            <div className="space-y-4">
              <div>
                <Label>Unit Cost ($)</Label>
                <Input
                  type="number"
                  min="0"
                  value={items[currentItemIndex].unit_cost}
                  onChange={(e) => updateItem(currentItemIndex, 'unit_cost', parseFloat(e.target.value) || 0)}
                  placeholder="Your cost per unit"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This is internal only - customer will not see this
                </p>
              </div>
              <div>
                <Label>Markup (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="500"
                  value={items[currentItemIndex].markup_percent}
                  onChange={(e) => updateItem(currentItemIndex, 'markup_percent', parseFloat(e.target.value) || 0)}
                  placeholder="Markup percentage"
                />
              </div>
              <div className="p-4 bg-gray-100 rounded-lg">
                <div className="flex justify-between text-sm mb-2">
                  <span>Unit Cost:</span>
                  <span>${items[currentItemIndex].unit_cost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Markup ({items[currentItemIndex].markup_percent}%):</span>
                  <span>${(items[currentItemIndex].unit_cost * items[currentItemIndex].markup_percent / 100).toFixed(2)}</span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between font-bold text-[#595959]">
                  <span>Unit Price:</span>
                  <span>${items[currentItemIndex].unit_price.toFixed(2)}</span>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowPricingDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={() => setShowPricingDialog(false)} className="bg-[#595959] hover:bg-[#404040]">
                  Apply
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
